import { gql, useApolloClient, useLazyQuery, useQuery } from '@apollo/client'
import { useEffect, useState, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useParams, useSearchParams } from 'react-router-dom'
import { Typography } from '@mui/material'

import { Button, CardBox, Container, NotificationDialog, Row, Stack, useAlert } from '../../../components'
import * as SecuritiesPayment from '../../../components/payment/securities'
import { mutations, queries } from '../../../graphql'
import { fromAmountField, toAmountString } from '../../../utils'

const GET_CONTRIBUTION_INTENDED_USE = gql`
  query contributionIntendedUse($contributionId: Int!, $fundId: Int!) {
    contributionIntendedUse(contributionId: $contributionId, fundId: $fundId)
  }
`

export function InitiatedContributions() {
  const client = useApolloClient()
  const { fundId } = useParams()
  const [searchParams] = useSearchParams()
  const targetContributionId = searchParams.get('contributionId')

  const { data: { me: { fund } = { fund: {} } } = {} } = useQuery(queries.funds.myFund)
  const { data: { me } = {} } = useQuery(queries.user.me)
  const { data: custodianSettingsData, loading: custodianSettingsLoading } = useQuery(
    queries.user.myAdvisorCustodianSettings
  )
  const advisorCustodianSettings = custodianSettingsData?.myAdvisorCustodianSettings

  const { loading, data: requestedData } = useQuery(queries.funds.myRequestedSecuritiesContributions, {
    variables: { fundId: Number(fundId) },
  })
  const requestedSecurities = requestedData?.myRequestedSecuritiesContributions || []

  const [{ Alert, alertProps }, { setAlert }] = useAlert()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [letterOfDirectionPdf, setLetterOfDirectionPdf] = useState(null)
  const [formReady, setFormReady] = useState(false)
  const [resolvedIntendedUse, setResolvedIntendedUse] = useState(null)

  const [fetchIntendedUse, { data: intendedUseData }] = useLazyQuery(GET_CONTRIBUTION_INTENDED_USE)
  const intendedUse = intendedUseData?.contributionIntendedUse || null

  // Select targeted contribution from badge click, or default to most recent
  const selectedContribution = useMemo(() => {
    if (requestedSecurities.length === 0) return null
    if (targetContributionId) {
      const target = requestedSecurities.find((c) => c.id === Number(targetContributionId))
      if (target) return target
    }
    return [...requestedSecurities].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]
  }, [requestedSecurities, targetContributionId])

  const {
    register,
    handleSubmit,
    formState,
    control,
    setValue,
    watch,
    reset: resetForm,
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      address: { province: '' },
      securities: [{ securitiesDescription: '', fundSymbolCUSIP: '', quantity: 0, amount: 0 }],
      pageLanguageConfirmed: false,
      intendedUseAcknowledged: false,
      advisorIntendedUseChoice: '',
    },
  })

  const { isSubmitting } = formState
  const advisorIntendedUseChoice = watch('advisorIntendedUseChoice')
  const effectiveIntendedUse = intendedUse === 'discuss' ? advisorIntendedUseChoice : intendedUse

  // Fetch intendedUse and reset acknowledgement fields when a contribution is selected
  useEffect(() => {
    if (selectedContribution?.id) {
      fetchIntendedUse({ variables: { contributionId: selectedContribution.id, fundId: Number(fundId) } })
      setValue('intendedUseAcknowledged', false)
      setValue('advisorIntendedUseChoice', '')
    }
  }, [selectedContribution?.id, fetchIntendedUse, setValue])

  // Pre-fill form with advisor's custodian settings
  useEffect(() => {
    if (!custodianSettingsLoading) {
      if (advisorCustodianSettings) {
        resetForm({
          custodianName: advisorCustodianSettings.custodianName || '',
          advisorName: advisorCustodianSettings.advisorName || '',
          advisorEmail: advisorCustodianSettings.advisorEmail || '',
          advisorPhone: advisorCustodianSettings.advisorPhone || '',
          advisorDealerRepCode: advisorCustodianSettings.advisorDealerRepCode || '',
          address: {
            lineOne: advisorCustodianSettings.address?.lineOne || '',
            city: advisorCustodianSettings.address?.city || '',
            province: advisorCustodianSettings.address?.province || '',
            postalCode: advisorCustodianSettings.address?.postalCode || '',
          },
          securities: [{ securitiesDescription: '', fundSymbolCUSIP: '', quantity: 0, amount: 0 }],
          pageLanguageConfirmed: false,
          intendedUseAcknowledged: false,
          advisorIntendedUseChoice: '',
        })
      }
      setFormReady(true)
    }
  }, [custodianSettingsLoading, advisorCustodianSettings, resetForm])

  const completeContribution = async (data) => {
    try {
      // Resolve the final intendedUse: for "discuss", use the advisor's choice
      const finalIntendedUse = intendedUse === 'discuss' ? data.advisorIntendedUseChoice : intendedUse

      const deliveringCustodianInfo = {
        securitiesPath: 'advisor-completed',
        accountNumber: data.accountNumber,
        custodianName: data.custodianName,
        advisorName: data.advisorName,
        advisorDealerRepCode: data.advisorDealerRepCode,
        advisorEmail: data.advisorEmail,
        advisorPhone: data.advisorPhone,
        address: data.address,
        intendedUse: finalIntendedUse,
        ...(finalIntendedUse === 'invest' && {
          investmentAccountNumber: data.investmentAccountNumber,
          investmentAccountName: data.investmentAccountName,
        }),
      }

      // Format securities for the mutation (strings, not numbers)
      const securitiesInput = data.securities.map((sec) => ({
        securitiesDescription: sec.securitiesDescription,
        fundSymbolCUSIP: sec.fundSymbolCUSIP,
        quantity: String(sec.quantity),
        amount: String(sec.amount),
      }))

      const result = await client.mutate({
        mutation: mutations.contributions.completeRequestedSecuritiesContribution,
        variables: {
          contributionId: selectedContribution.id,
          fundId: Number(fundId),
          deliveringCustodianInfo,
          securities: securitiesInput,
        },
        refetchQueries: [
          { query: queries.funds.myFund, variables: { fundId: Number(fundId) } },
          { query: queries.funds.myRequestedSecuritiesContributions, variables: { fundId: Number(fundId) } },
        ],
        awaitRefetchQueries: true,
      })

      // Form has all advisor fields, use them directly for PDF
      const pdfCustodianInfo = { ...data }

      setResolvedIntendedUse(finalIntendedUse)
      if (finalIntendedUse === 'invest' || finalIntendedUse === 'liquidate') {
        setLetterOfDirectionPdf(
          SecuritiesPayment.fetchGeneratedLetterOfDirectionPdf({
            me,
            fundId,
            securities: data?.securities,
            deliveringCustodianInfo: pdfCustodianInfo,
            contributionIds: result.data.completeRequestedSecuritiesContribution.map((c) => c.id),
          })
        )
      }
      setDialogOpen(true)
    } catch (e) {
      setAlert({
        message: `Failed to complete securities contribution: ${e.message ?? ''}`,
        error: e.message,
        severity: 'error',
      })
    }
  }

  const onSubmit = async (data) => {
    const convertedData = {
      ...data,
      securities: data.securities.map((sec) => ({
        ...sec,
        quantity: parseFloat(fromAmountField(sec.quantity, 4)),
        amount: parseFloat(fromAmountField(sec.amount)),
      })),
    }

    completeContribution(convertedData)
  }

  const closeSuccessDialog = () => {
    resetForm()
    setDialogOpen(false)
    setLetterOfDirectionPdf(null)
    setResolvedIntendedUse(null)
  }

  return (
    <Container maxWidth="lg">
      <CardBox title="Complete Securities Contribution">
        <Stack spacing={3}>
          {/* No pending contributions message */}
          {!loading && !selectedContribution && (
            <Typography variant="body2" color="text.secondary">
              No requested securities contributions pending.
            </Typography>
          )}

          {/* Contribution info */}
          {selectedContribution && (
            <Typography variant="body1">
              Requested contribution amount: <strong>{toAmountString(selectedContribution.amount)}</strong>
            </Typography>
          )}

          {selectedContribution && (custodianSettingsLoading || !formReady) && (
            <Typography variant="body2" color="text.secondary">
              Loading...
            </Typography>
          )}

          {selectedContribution && formReady && (
            <form
              onSubmit={handleSubmit(onSubmit, (errors) => {
                const checkboxFields = ['pageLanguageConfirmed', 'intendedUseAcknowledged', 'advisorIntendedUseChoice']
                const hasCheckboxError = checkboxFields.some((field) => errors[field])
                if (hasCheckboxError) {
                  setAlert({
                    message: 'Please check all required acknowledgement boxes before submitting.',
                    severity: 'error',
                  })
                } else {
                  setAlert({
                    message: 'Please fill in all required fields before submitting.',
                    severity: 'error',
                  })
                }
              })}
            >
              <Stack>
                {intendedUse && (
                  <SecuritiesPayment.IntendedUseAcknowledgement
                    intendedUse={intendedUse}
                    control={control}
                    formState={formState}
                  />
                )}
                <SecuritiesPayment.DeliveringCustodianInformation
                  {...{ register, control, formState }}
                  shrinkLabels
                  intendedUse={effectiveIntendedUse}
                />
                <SecuritiesPayment.SecuritiesToTransferInKind {...{ register, control, formState }} />
                <SecuritiesPayment.Acknowledgement {...{ control, formState }} />

                <Row sx={{ marginTop: '2rem!important' }} alignSelf="flex-end">
                  <Button type="submit" disabled={isSubmitting} data-testid="createInitiatedSecuritiesContribution">
                    Submit
                  </Button>
                </Row>
                <Alert {...alertProps} />
              </Stack>
            </form>
          )}
        </Stack>
      </CardBox>

      <NotificationDialog
        open={dialogOpen}
        onClose={closeSuccessDialog}
        type="success"
        title="Transfer Details Submitted"
        message={
          resolvedIntendedUse === 'invest' ? (
            <>
              <p>
                <em>(Internal In-Kind Securities Donation)</em>
              </p>
              <p>
                You have successfully provided the details for your client&apos;s donation of securities. Please find
                the Letter of Direction here.
              </p>
              <SecuritiesPayment.GeneratedLetterOfDirection letterOfDirectionPdf={letterOfDirectionPdf} />
              <p>
                To complete the donation process, you will need to use these details to complete your dealer&apos;s
                internal in-kind transfer forms. This donation will not be fully initiated until you submit the internal
                transfer forms.
              </p>
              <p>
                For your convenience, you will receive an email to assist you with filling in your dealer&apos;s forms.
              </p>
              <p>
                Once the transaction is complete and asset has settled, please email to{' '}
                <a href="mailto:accounting@givewise.ca">accounting@givewise.ca</a>.
              </p>
            </>
          ) : (
            <>
              <p>You have successfully initiated a securities transfer-in-kind for your client.</p>
              <SecuritiesPayment.GeneratedLetterOfDirection letterOfDirectionPdf={letterOfDirectionPdf} />
              <p>
                To complete this contribution, you will need to submit the attached Letter of Direction to your
                dealer/broker with instructions for them to transfer the asset to GiveWise&apos;s BMO Investorline
                brokerage account. GiveWise does not initiate the transfer from our end.
              </p>
              <p>This information will also be emailed to you, for your records.</p>
              <p>
                Please be aware that a tax receipt will not be populated until the security has been received and is
                liquidated. NOTE: There may be adjustments due to fluctuations in value and exchange.
              </p>
            </>
          )
        }
        actions={[
          {
            label: 'Done',
            onClick: closeSuccessDialog,
            to:
              resolvedIntendedUse === 'invest'
                ? `/funds/${fundId}/activity/investment-account`
                : `/funds/${fundId}/activity/giving-wallet`,
          },
        ]}
      />
    </Container>
  )
}
